"use client";

import React from "react";
import { useContext, useEffect, useState } from "react";
import { ReactSVG } from "react-svg";

import { blackFrame } from "@/components/QRFrame/frames/black";
import { boardFrame } from "@/components/QRFrame/frames/board";
import { buttonFrame } from "@/components/QRFrame/frames/button";
import { cafeFrame } from "@/components/QRFrame/frames/cafe";
import { envelopeFrame } from "@/components/QRFrame/frames/envelope";
import { mobileFrame } from "@/components/QRFrame/frames/mobile";
import { museumFrame } from "@/components/QRFrame/frames/museum";
import { receiptFrame } from "@/components/QRFrame/frames/receipt";
import { shoppingFrame } from "@/components/QRFrame/frames/shopping";
import { ticketFrame } from "@/components/QRFrame/frames/ticket";
import { whiteFrame } from "@/components/QRFrame/frames/white";

import { QREditorContext, INITIAL_QR_DATA } from "@/contexts/QREditorContext";

import { useQRStyle } from "@/hooks/useQR";

import { isSafari, isClient } from "@/utils/browser";
import { getQRCodeUrl } from "@/utils/qrEditorHelper";
import { getUnlockFeaturesModal } from "@/modals/UnlockFeaturesModal";
import { ModalContext } from "@/contexts/ModalContext";
import { QREditorModalDataProps } from "@/templates/QrEditorTemplates/qr-layout/types";
import { useLogin } from "@/hooks/auth/useLogin";
import { hairsalonFrame } from "../QRFrame/frames/hairsalon";
import { beautyFrame } from "../QRFrame/frames/beauty";
import { careFrame } from "../QRFrame/frames/care";
import { waiterFrame } from "../QRFrame/frames/waiter";
import { bucketFrame } from "../QRFrame/frames/bucket";
import { takeawayFrame } from "../QRFrame/frames/takeaway";
import { cupFrame } from "../QRFrame/frames/cup";
import { brochureFrame } from "../QRFrame/frames/brochure";
import { asianFrame } from "../QRFrame/frames/asian";
import { sodaFrame } from "../QRFrame/frames/soda";
import { menuFrame } from "../QRFrame/frames/menu";
import { restaurantFrame } from "../QRFrame/frames/restaurant";
import { wineFrame } from "../QRFrame/frames/wine";
import { headphonesFrame } from "../QRFrame/frames/headphones";
import { micFrame } from "../QRFrame/frames/mic";
import { concertFrame } from "../QRFrame/frames/concert";
import { videogameFrame } from "../QRFrame/frames/videogame";
import { movieFrame } from "../QRFrame/frames/movie";
import { musicFrame } from "../QRFrame/frames/music";
import { mp3Frame } from "../QRFrame/frames/mp3";
import { filmFrame } from "../QRFrame/frames/film";
import { handFrame } from "../QRFrame/frames/hand";
import { likeFrame } from "../QRFrame/frames/like";
import { chatFrame } from "../QRFrame/frames/chat";
import { starsFrame } from "../QRFrame/frames/stars";
import { reviewFrame } from "../QRFrame/frames/review";
import { ticket2Frame } from "../QRFrame/frames/ticket2";
import { weddingFrame } from "../QRFrame/frames/wedding";
import { christmasFrame } from "../QRFrame/frames/christmas";
import { partyFrame } from "../QRFrame/frames/party";
import { ticketsFrame } from "../QRFrame/frames/tickets";
import { celebrationFrame } from "../QRFrame/frames/celebration";
import { partyhatFrame } from "../QRFrame/frames/partyhat";
import { balloonsFrame } from "../QRFrame/frames/balloons";
import { garlandFrame } from "../QRFrame/frames/garland";
import { bouquetFrame } from "../QRFrame/frames/bouquet";
import { giftboxFrame } from "../QRFrame/frames/giftbox";
import { cardsFrame } from "../QRFrame/frames/cards";
import { shoppingbagFrame } from "../QRFrame/frames/shoppingbag";
import { cartFrame } from "../QRFrame/frames/cart";
import { eyelashesFrame } from "../QRFrame/frames";

export const QRPreviewStyle = ({
  data,
  modalData,
}: {
  data?: any;
  modalData?: QREditorModalDataProps;
}) => {
  const qrEditorContext = useContext(QREditorContext);
  const { isUserLoggedIn } = useLogin();

  const { ref, updateQRCode, url, setUrl, qrCode } = useQRStyle();
  const frame = qrEditorContext.qrData.qrStyle;

  const [urlQr, setUrlQr] = useState(undefined);

  const getFrameWrapperStyle = () => {
    return `qr-preview__canvas__wrapper ${
      frame?.frameStyle === "none" ||
      frame?.frameStyle === undefined ||
      qrEditorContext.qrData?.qrStyle?.styleType === "AI"
        ? "qr-preview__canvas__wrapper--none"
        : ""
    }`;
  };

  const getQrCode = () => {
    const qrStyleType = qrEditorContext.qrData?.qrStyle?.styleType;
    const selectedQrCode = qrEditorContext.qrData?.ai?.selectedImageUrl;

    return qrStyleType === "AI" ? (
      selectedQrCode ? (
        <img src={selectedQrCode} />
      ) : (
        <img src="/images/empty-logo.png" />
      )
    ) : (
      <div ref={ref}></div>
    );
  };

  const getFrame = () => {
    const initialFrameValues = INITIAL_QR_DATA.qrStyle;
    const qrStyleType = qrEditorContext.qrData?.qrStyle?.styleType;

    const sharedProps = {
      children: <div ref={ref}></div>,
      frameBackgroundColor: frame?.frameBackgroundColor ?? initialFrameValues.frameBackgroundColor,
      frameColor: frame?.frameColor ?? initialFrameValues.frameColor,
      text: frame?.frameText ?? initialFrameValues.frameText,
      urlQr: isSafari() ? data?.image : urlQr,
      textColor: frame?.frameTextColor ?? initialFrameValues.frameTextColor,
    };

    if (qrStyleType === "AI") return getQrCode();

    switch (frame?.frameStyle) {
      case undefined:
      case "none":
        return getQrCode();
      case "ticket":
        return ticketFrame(sharedProps);
      case "mobile":
        return mobileFrame(sharedProps);
      case "black":
        return blackFrame(sharedProps);
      case "white":
        return whiteFrame(sharedProps);
      case "button":
        return buttonFrame(sharedProps);
      case "museum":
        return museumFrame(sharedProps);
      case "cafe":
        return cafeFrame(sharedProps);
      case "shopping":
        return shoppingFrame(sharedProps);
      case "board":
        return boardFrame(sharedProps);
      case "envelope":
        return envelopeFrame(sharedProps);
      case "receipt":
        return receiptFrame(sharedProps);
      case "eyelashes":
        return eyelashesFrame(sharedProps);
      case "hairsalon":
        return hairsalonFrame(sharedProps);
      case "beauty":
        return beautyFrame(sharedProps);
      case "care":
        return careFrame(sharedProps);
      case "waiter":
        return waiterFrame(sharedProps);
      case "bucket":
        return bucketFrame(sharedProps);
      case "takeaway":
        return takeawayFrame(sharedProps);
      case "cup":
        return cupFrame(sharedProps);
      case "brochure":
        return brochureFrame(sharedProps);
      case "asian":
        return asianFrame(sharedProps);
      case "soda":
        return sodaFrame(sharedProps);
      case "menu":
        return menuFrame(sharedProps);
      case "restaurant":
        return restaurantFrame(sharedProps);
      case "wine":
        return wineFrame(sharedProps);
      case "headphones":
        return headphonesFrame(sharedProps);
      case "mic":
        return micFrame(sharedProps);
      case "concert":
        return concertFrame(sharedProps);
      case "videogame":
        return videogameFrame(sharedProps);
      case "movie":
        return movieFrame(sharedProps);
      case "music":
        return musicFrame(sharedProps);
      case "mp3":
        return mp3Frame(sharedProps);
      case "film":
        return filmFrame(sharedProps);
      case "hand":
        return handFrame(sharedProps);
      case "like":
        return likeFrame(sharedProps);
      case "chat":
        return chatFrame(sharedProps);
      case "stars":
        return starsFrame(sharedProps);
      case "review":
        return reviewFrame(sharedProps);
      case "ticket2":
        return ticket2Frame(sharedProps);
      case "wedding":
        return weddingFrame(sharedProps);
      case "christmas":
        return christmasFrame(sharedProps);
      case "party":
        return partyFrame(sharedProps);
      case "tickets":
        return ticketsFrame(sharedProps);
      case "celebration":
        return celebrationFrame(sharedProps);
      case "partyhat":
        return partyhatFrame(sharedProps);
      case "balloons":
        return balloonsFrame(sharedProps);
      case "garland":
        return garlandFrame(sharedProps);
      case "bouquet":
        return bouquetFrame(sharedProps);
      case "giftbox":
        return giftboxFrame(sharedProps);
      case "cards":
        return cardsFrame(sharedProps);
      case "shoppingbag":
        return shoppingbagFrame(sharedProps);
      case "cart":
        return cartFrame(sharedProps);
    }
  };

  useEffect(() => {
    if (isClient() && isSafari() && qrCode) {
      setTimeout(() => {
        qrCode?.getRawData("png").then((data) => data && setUrlQr(URL.createObjectURL(data)));
      }, 100);
    }
  }, [qrCode]);

  useEffect(() => {
    updateQRCode({
      qrUrl: url,
      qrStyle: data?.qrStyle,
    });
  }, [data, url]);

  useEffect(() => {
    if (qrEditorContext?.qrData.qrCodeReference) {
      const qrUrl = getQRCodeUrl(qrEditorContext.qrData, true);
      if (qrUrl) setUrl(qrUrl);
    }
  }, [qrEditorContext?.qrData.qrCodeReference]);

  const modalContext = React.useContext(ModalContext);

  const handleContextMenu = (event) => {
    if (isUserLoggedIn()) return;

    event.preventDefault();

    modalContext.handleModal({
      content: getUnlockFeaturesModal({
        onConfirmButton: () => {
          modalContext.handleModal();
        },
        data: modalData,
      }),
    });
  };

  return (
    <div className="qr-preview__layout__image">
      <div
        className="qr-preview__canvas"
        id="canvas">
        <div className="frame">
          <div
            className={getFrameWrapperStyle()}
            onContextMenu={handleContextMenu}>
            {getFrame() as any}
          </div>
        </div>
      </div>

      <ReactSVG
        src={`/images/phone-previews/phone-empty.svg`}
        className="preview-frame"
      />
    </div>
  );
};
